import { ButtonText, Flex, SidePanel, Typography } from '@percent/lemonade'
import { useTranslation } from 'react-i18next'
import { CandidateTasksProps } from './CandidateTasks.types'
import { useEffect, useMemo, useCallback, useState } from 'react'
import { StringParam, useQueryParams } from 'use-query-params'
import { TasksList } from './tasksList/TasksList'
import { CandidateTaskForm } from './candidateTaskForm/CandidateTaskForm'
import { TaskSubmissionView } from './taskSubmissionView/TaskSubmissionView'
import { useParams } from 'react-router-dom'
import { TaskType } from '@percent/cause-dashboard/services/grants/grantsService.types'
import { useAuthState } from '@percent/cause-dashboard/common/hooks'
import { useTasksAutosavePreference, TaskAutosaveModal, TaskAutosaveToggle } from '@percent/domain/grants'
import { useTaskAssignees } from './tasksList/hooks/useTaskAssignees'

export function CandidateTasks({
  tasksOpen,
  tasks,
  programId,
  candidateData,
  refetchTasks,
  refetchDocuments,
  onOpenDocuments
}: Readonly<CandidateTasksProps>) {
  const { candidateId } = useParams<{ candidateId: string }>()

  const { authState } = useAuthState()

  const userId = authState.user?.id

  const { getAssignee } = useTaskAssignees()

  const [queryParams, setQueryParams] = useQueryParams({ tasksView: StringParam, taskId: StringParam })
  const [selectedTaskId, setSelectedTaskId] = useState(queryParams.taskId)
  const { shouldShowModal, isAutosaveEnabled, enableAutosave, disableAutosave } = useTasksAutosavePreference()
  const showAutoSaveToggle = !shouldShowModal

  const { t } = useTranslation()

  const resetTaskId = () => {
    refetchTasks()
    setQueryParams({ taskId: undefined })
  }
  const selectTask = (taskId: string) => setQueryParams({ taskId })
  const hasTasks = tasks.length > 0

  const closeTasksView = () => {
    setQueryParams({ tasksView: undefined, taskId: undefined })
  }

  const onFormSubmitted = useCallback(() => {
    refetchTasks()
    refetchDocuments()
    setQueryParams({ tasksView: 'true', taskId: undefined })
  }, [refetchTasks, refetchDocuments, setQueryParams])

  useEffect(() => {
    setSelectedTaskId(queryParams.taskId)
  }, [queryParams.taskId])

  const form = useMemo(() => {
    const selectedTask = tasks.find(task => task.id === selectedTaskId)
    const isCompleted = selectedTask?.status === 'done'

    const isAssignee = selectedTask?.assignment.assignedTo === userId
    const isDisabled = !isAssignee
    const assignee = getAssignee(selectedTask?.id ?? '', selectedTask?.assignment.assignedTo)

    if (isCompleted && selectedTask?.type === TaskType.REVIEW) {
      return (
        <TaskSubmissionView
          programId={programId}
          candidateId={candidateId ?? ''}
          taskId={selectedTaskId ?? ''}
          formId={selectedTask?.form?.formId ?? ''}
          onDocumentsClick={onOpenDocuments}
        />
      )
    }

    return (
      <CandidateTaskForm
        formId={selectedTask?.form?.formId ?? ''}
        programId={programId}
        candidateId={candidateId ?? ''}
        taskId={selectedTaskId ?? ''}
        onFormSubmitted={onFormSubmitted}
        disabled={isDisabled}
        autosaveEnabled={isAutosaveEnabled}
        candidateData={candidateData}
        assigneeEmail={assignee?.email}
      />
    )
  }, [
    selectedTaskId,
    programId,
    candidateId,
    onFormSubmitted,
    tasks,
    userId,
    onOpenDocuments,
    isAutosaveEnabled,
    candidateData,
    getAssignee
  ])

  if (!tasksOpen || !hasTasks) {
    return null
  }

  return (
    <>
      <TaskAutosaveModal
        open={shouldShowModal}
        onClose={disableAutosave}
        onConfirm={enableAutosave}
        title={t('grants.grantDetails.candidateTasks.autoSaveModal.title')}
        description={t('grants.grantDetails.candidateTasks.autoSaveModal.description')}
        alertTitle={t('grants.grantDetails.candidateTasks.autoSaveModal.importantTitle')}
        alertDescription={t('grants.grantDetails.candidateTasks.autoSaveModal.importantDescription')}
        enableLabel={t('grants.grantDetails.candidateTasks.autoSaveModal.turnOn')}
        dismissLabel={t('grants.grantDetails.candidateTasks.autoSaveModal.notNow')}
      />
      <SidePanel
        title={t('grants.grantDetails.candidateTasks.title')}
        onClose={closeTasksView}
        testId="candidate-tasks"
        closeButtonTestId="close-tasks-view-button"
      >
        {selectedTaskId && (
          <Flex direction="column" data-testid="single-task-view">
            <ButtonText icon="arrow-left" data-testid="other-tasks-button" onClick={resetTaskId}>
              {t('grants.grantDetails.candidateTasks.otherTasks')}
            </ButtonText>

            {form}
          </Flex>
        )}

        {!selectedTaskId && (
          <Flex direction="column">
            {showAutoSaveToggle && (
              <TaskAutosaveToggle
                enabled={isAutosaveEnabled}
                onToggle={enabled => (enabled ? enableAutosave() : disableAutosave())}
                title={t('grants.grantDetails.candidateTasks.autoSaveToggle.title')}
                description={t('grants.grantDetails.candidateTasks.autoSaveToggle.description')}
              />
            )}
            <Typography>{t('grants.grantDetails.candidateTasks.description')}</Typography>
            <TasksList tasks={tasks} selectTask={selectTask} refetchTasks={refetchTasks} />
          </Flex>
        )}
      </SidePanel>
    </>
  )
}
